import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {formsApi} from "./forms-api";
import {Icon, useT} from "./host";

// «from form …» on a task that a form response created (task lists, boards and the task panel; docs/tasks/42-forms-task-source.md).
// One request for the whole workspace, shared by every card, read again after a minute.
const FRESH_MS = 60000;
const store = {map: new Map(), loadedAt: 0, loading: null, listeners: new Set()};

export function loadTaskSources(force = false) {
    if (store.loading) return store.loading;
    if (!force && Date.now() - store.loadedAt < FRESH_MS) return Promise.resolve(store.map);
    store.loading = formsApi.taskSources()
        .then((rows) => {
            store.map = new Map((rows || []).map((row) => [String(row.task), row]));
            store.loadedAt = Date.now();
            store.listeners.forEach((listener) => listener(store.map));
            return store.map;
        })
        .catch(() => store.map)
        .finally(() => { store.loading = null; });
    return store.loading;
}

export function useTaskSource(task) {
    const [map, setMap] = useState(store.map);
    useEffect(() => {
        store.listeners.add(setMap);
        loadTaskSources();
        return () => { store.listeners.delete(setMap); };
    }, []);
    return map.get(String(task?.documentId || "")) || null;
}

// detailed: the task panel's line «via form X · respondent: Y»; otherwise the short tag of cards and rows
export default function TaskFormTag({task, detailed = false, className = ""}) {
    const t = useT();
    const navigate = useNavigate();
    const source = useTaskSource(task);
    if (!source) return null;
    const open = (event) => {
        event.stopPropagation();
        if (source.canOpen) navigate(`/forms/${source.form.key}?step=responses&submission=${source.submission.key}`);
    };
    const label = detailed
        ? t("forms.task-source.detailed", {title: source.form.title, name: source.respondent || t("forms.detail.no-name")})
        : t("forms.task-source.short", {title: source.form.title});
    return (
        <span className={`inline-flex align-items-center gap-1 max-w-full min-w-0 overflow-hidden border-1 border-blue-200 bg-blue-50 text-blue-700 ${detailed ? "border-round-xl" : "border-round-3xl"} px-2 py-1 text-xs ${source.canOpen ? "cursor-pointer" : ""} ${className}`.trim()}
              title={t(source.canOpen ? "forms.task-source.open" : "forms.task-source.hint", {title: source.form.title})}
              role={source.canOpen ? "button" : undefined} tabIndex={source.canOpen ? 0 : undefined}
              onPointerDown={(event) => event.stopPropagation()} onClick={open}
              onKeyDown={(event) => { if (event.key === "Enter") open(event); }}>
            <Icon icon="mi mi:dynamic_form" className="text-sm flex-none"/>
            <span className={detailed ? "line-height-3" : "white-space-nowrap overflow-hidden text-overflow-ellipsis"}>{label}</span>
        </span>
    );
}
